import type { PathSegment } from '../types'

interface Position3D {
  x: number
  z: number
  floor: number
}

interface GraphNode {
  id: string
  x: number
  z: number
  floor: number
  edges: { nodeId: string; cost: number }[]
  isTransition: boolean
  connectedFloors: number[]
}

function nodeKey(x: number, z: number, floor: number): string {
  return `${x.toFixed(2)}_${z.toFixed(2)}_${floor}`
}

function distance(a: { x: number; z: number }, b: { x: number; z: number }): number {
  return Math.sqrt((a.x - b.x) ** 2 + (a.z - b.z) ** 2)
}

export class PathfindingEngine {
  nodes: Map<string, GraphNode> = new Map()

  buildGraph(segments: PathSegment[]) {
    this.nodes.clear()

    for (const seg of segments) {
      const startKey = nodeKey(seg.start_x, seg.start_z, seg.floor)
      const endKey = nodeKey(seg.end_x, seg.end_z, seg.floor)

      if (!this.nodes.has(startKey)) {
        this.nodes.set(startKey, {
          id: startKey,
          x: seg.start_x,
          z: seg.start_z,
          floor: seg.floor,
          edges: [],
          isTransition: seg.start_is_transition,
          connectedFloors: seg.start_is_transition ? seg.connected_floors : [],
        })
      }

      if (!this.nodes.has(endKey)) {
        this.nodes.set(endKey, {
          id: endKey,
          x: seg.end_x,
          z: seg.end_z,
          floor: seg.floor,
          edges: [],
          isTransition: seg.end_is_transition,
          connectedFloors: seg.end_is_transition ? seg.connected_floors : [],
        })
      }

      const cost = seg.cost ?? distance(
        { x: seg.start_x, z: seg.start_z },
        { x: seg.end_x, z: seg.end_z }
      )

      this.nodes.get(startKey)!.edges.push({ nodeId: endKey, cost })
      if (seg.bidirectional) {
        this.nodes.get(endKey)!.edges.push({ nodeId: startKey, cost })
      }
    }

    // Connect transition nodes across floors
    const transitionNodes = [...this.nodes.values()].filter((n) => n.isTransition)
    for (const node of transitionNodes) {
      for (const targetFloor of node.connectedFloors) {
        if (targetFloor === node.floor) continue
        // Find nearest transition node on target floor
        const targetNodes = transitionNodes.filter(
          (n) => n.floor === targetFloor && n.id !== node.id
        )
        for (const target of targetNodes) {
          const dist = distance(node, target)
          if (dist < 2) {
            // Same position on different floor = transition
            node.edges.push({ nodeId: target.id, cost: 1 })
          }
        }
      }
    }
  }

  findPath(start: Position3D, end: Position3D): Position3D[] {
    const startNode = this.findNearestNode(start)
    const endNode = this.findNearestNode(end)
    if (!startNode || !endNode) return []

    // Dijkstra
    const dist = new Map<string, number>()
    const prev = new Map<string, string | null>()
    const visited = new Set<string>()
    const queue: { id: string; cost: number }[] = []

    for (const node of this.nodes.values()) {
      dist.set(node.id, Infinity)
      prev.set(node.id, null)
    }
    dist.set(startNode.id, 0)
    queue.push({ id: startNode.id, cost: 0 })

    while (queue.length > 0) {
      // Simple priority queue (sort by cost)
      queue.sort((a, b) => a.cost - b.cost)
      const current = queue.shift()!
      if (visited.has(current.id)) continue
      visited.add(current.id)

      if (current.id === endNode.id) break

      const node = this.nodes.get(current.id)
      if (!node) continue

      for (const edge of node.edges) {
        if (visited.has(edge.nodeId)) continue
        const newDist = dist.get(current.id)! + edge.cost
        if (newDist < dist.get(edge.nodeId)!) {
          dist.set(edge.nodeId, newDist)
          prev.set(edge.nodeId, current.id)
          queue.push({ id: edge.nodeId, cost: newDist })
        }
      }
    }

    // Reconstruct path
    const path: Position3D[] = []
    let currentId: string | null = endNode.id
    while (currentId) {
      const node = this.nodes.get(currentId)
      if (node) {
        path.unshift({ x: node.x, z: node.z, floor: node.floor })
      }
      currentId = prev.get(currentId) ?? null
    }

    // Add start and end points
    if (path.length > 0) {
      path.unshift(start)
      path.push(end)
    }

    return path
  }

  findNearestNode(point: Position3D): GraphNode | null {
    let nearest: GraphNode | null = null
    let minDist = Infinity

    for (const node of this.nodes.values()) {
      if (node.floor !== point.floor) continue
      const d = distance(node, point)
      if (d < minDist) {
        minDist = d
        nearest = node
      }
    }

    return nearest
  }
}
